/**
 * `causal.cycle` (§5.2 Soft-constraint rules; `06-timeline.md` §8; #222) — a
 * directed cycle in the composed causal/precedence graph of Events. A loop in
 * "happens-before" is a paradox: an event that (transitively) causes or
 * precedes itself. The canonical case is two events each `caused_by` the other,
 * or a `caused_by` and a `precedes` that disagree on the order of the same pair.
 *
 * Severity: `error` — a genuine consistency violation the causal-chain view
 * cannot order, surfaced prominently with a one-click "break the loop" fix. It
 * is deliberately NOT `blocking_error`: the GM must be able to author the fact
 * that closes the loop (and only then see the cycle) before deciding which edge
 * to drop — refusing the write would make the cycle un-introducible and so
 * un-fixable through the normal editor flow.
 *
 * Why configurable causal predicates: predicate identity is an OPAQUE id
 * (ADR-0004 §1) with no semantic `caused_by`/`precedes` flag on the
 * declaration, and the engine stays English-free and pack-agnostic. So the set
 * of causal predicates (and each one's edge direction) is passed in via
 * {@link AuditContext.causal_predicates}; the client supplies the official-core
 * binding (`OFFICIAL_CORE_CAUSAL_PREDICATES`). Absent / empty ⇒ the rule is a
 * no-op (progressive disclosure).
 *
 * Owner-scoped (consistent with the other cross-fact rules): only CANON causal
 * facts build the canon graph. A belief that one event caused another lives in
 * a believer's own layer and never forms a canon paradox.
 *
 * This is the first cycle-detection rule in the engine; it establishes the
 * pattern the Geography v1 `containment.cycle` rule will reuse (directed-graph
 * fold → `analyzeDirectedGraph` → one advisory per canonical loop).
 */
import {
  analyzeDirectedGraph,
  causalEdge,
  currentFactValue,
  splitFactKey,
  type CausalPredicateSpec,
  type DirectedEdge,
} from '@dl/shared';

import { factRef, type RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** The rule id — advisory `rule` + the §5.2.1 override key. */
export const CAUSAL_CYCLE_RULE = 'causal.cycle';

/** Where a happens-before edge came from, so a detected loop can cite + fix it. */
interface EdgeMeta {
  subject: string;
  /** The bare predicate key on the subject (no instance suffix → fixable). */
  factKey: string;
}

export function causalCycle(ctx: AuditContext): RawAdvisory[] {
  const specs = ctx.causal_predicates;
  if (!specs || specs.length === 0) return [];
  const directionByPredicate = new Map<string, CausalPredicateSpec['direction']>();
  for (const s of specs) directionByPredicate.set(s.predicate, s.direction);

  // Fold every live CANON causal fact into a happens-before edge. The edge's
  // `via` is a factRef so a detected loop points back at the offending facts;
  // `edgeMeta` carries the (subject, factKey) for the break-the-loop fix.
  const edges: DirectedEdge[] = [];
  const edgeMeta = new Map<string, EdgeMeta>();
  const nodes = new Set<string>();

  for (const entity of Object.values(ctx.entities)) {
    if (entity.soft_deleted) continue;
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      if (fact.owner.kind !== 'canon') continue;
      const { predicate } = splitFactKey(factKey);
      const direction = directionByPredicate.get(predicate);
      if (direction === undefined) continue;
      const value = currentFactValue(fact);
      // A causal fact's value is a bare target entity-id string (one per
      // {object}-uniqueness segment). Skip non-string / dangling targets — the
      // ref.dangling rule owns broken references; a cycle needs two live ends.
      if (typeof value !== 'string' || value.length === 0) continue;
      const target = ctx.entities[value];
      if (target === undefined || target.soft_deleted) continue;

      const via = factRef(entity.id, factKey);
      edges.push(causalEdge(entity.id, value, direction, via));
      // Only a bare predicate slot (no instance suffix) is safely fixable via
      // the client's predicate-keyed soft-delete; otherwise omit the fix meta.
      if (splitFactKey(factKey).instance === null) {
        edgeMeta.set(via, { subject: entity.id, factKey });
      }
      nodes.add(entity.id);
      nodes.add(value);
    }
  }

  if (edges.length === 0) return [];

  const { cycles } = analyzeDirectedGraph(nodes, edges);
  if (cycles.length === 0) return [];

  const out: RawAdvisory[] = [];
  for (const cycle of cycles) {
    const factIds = cycle.edges.filter((v): v is string => v !== undefined);
    // Offer to break the loop by removing the first fixable edge fact (one
    // edge's removal is enough to break a simple cycle). Deterministic: the
    // canonicalised cycle's edges are in a stable order.
    let fix: RawAdvisory['fix'];
    for (const via of cycle.edges) {
      const meta = via !== undefined ? edgeMeta.get(via) : undefined;
      if (meta) {
        fix = {
          kind: 'remove_fact',
          subject: meta.subject,
          factKey: meta.factKey,
          label_key: 'auditor.fix.break_cycle',
          params: { length: cycle.nodes.length },
        };
        break;
      }
    }

    out.push({
      severity: 'error',
      rule: CAUSAL_CYCLE_RULE,
      message_key: 'auditor.causal.cycle',
      params: { length: cycle.nodes.length },
      fact_ids: factIds,
      entity_ids: [...cycle.nodes],
      ...(fix ? { fix } : {}),
    });
  }

  return out;
}
